import React, { useEffect, useState } from 'react';
import { Box, Button, Center, Input, Text, VStack, useToast, Spinner } from '@chakra-ui/react';
import { supabasePublic } from '../supabaseClient';
import { getBackendURL, basename } from '../env';
import { fetchAndLogPatientSignupDiagnostics } from '../../utils/patientSignupDebug';

export default function PatientVerification() {
  const toast = useToast();
  const [checking, setChecking] = useState(true);
  const [userId, setUserId] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentMethod, setSentMethod] = useState(null);
  const [pendingReview, setPendingReview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabasePublic().auth.getSession();
      if (cancelled) return;
      if (!session?.user?.id) {
        window.location.href = window.location.origin + (basename || '') + '/auth';
        return;
      }
      setUserId(session.user.id);

      // Decide up front which screen to show. In manual-approval mode every
      // signup is written as 'pending_review', so the review notice must appear
      // BEFORE (instead of) the code-delivery screen. Only an auto-approve match
      // ('pending_verification') should reach the get-a-code step.
      const { data: row } = await supabasePublic()
        .from('users')
        .select('patient_match_status')
        .eq('id', session.user.id)
        .single();
      if (cancelled) return;
      const status = row?.patient_match_status;
      if (status === 'verified') {
        window.location.href = window.location.origin + (basename || '') + '/schedules';
        return;
      }
      if (status !== 'pending_verification') {
        setPendingReview(true);
      }
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const requestCode = async (deliveryMethod) => {
    setLoading(true);
    try {
      const res = await fetch(`${getBackendURL()}/api/patient-signup/request-verification/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, delivery_method: deliveryMethod }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.status === 'verified') {
        toast({ title: 'Patient portal verified', status: 'success', duration: 2000 });
        window.location.href = window.location.origin + (basename || '') + '/schedules';
      } else if (data.status === 'pending_review') {
        setPendingReview(true);
      } else if (res.ok && data.status === 'verification_sent') {
        setSent(true);
        setSentMethod(data.delivery_method || deliveryMethod);
        toast({ title: 'Verification code sent', status: 'success', duration: 3000 });
      } else {
        // Show the backend's reason. Every distinct failure (send_failed,
        // missing_signup_email, server_error, …) used to collapse into one
        // opaque message, which left production failures undiagnosable.
        const reason = [data.reason || data.status || `http_${res.status}`, data.detail]
          .filter(Boolean)
          .join(' ');
        console.error('[patient-verification] send failed', { httpStatus: res.status, data });
        // Print the backend's plain-English health report to the console so the
        // real cause (missing env var / table / column, dead mail token, stale
        // cache) is visible in devtools — not just the opaque toast. Fetched
        // only on an actual send failure, never on the happy path.
        fetchAndLogPatientSignupDiagnostics('verification send failed');
        toast({
          title: 'Could not send verification code',
          description: `Please try again in a moment or contact staff for help. (${reason})`,
          status: 'error',
          duration: 8000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmCode = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getBackendURL()}/api/patient-signup/confirm-verification/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === 'verified') {
        toast({ title: 'Patient portal verified', status: 'success', duration: 2000 });
        window.location.href = window.location.origin + (basename || '') + '/schedules';
      } else if (res.ok && data.status === 'pending_review') {
        toast({ title: 'Contact verified', description: 'Your request is now pending staff review.', status: 'success', duration: 3000 });
        setPendingReview(true);
      } else {
        toast({
          title: 'Verification failed',
          description: 'Check the code and try again. If the problem continues, contact staff for help.',
          status: 'error',
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return <Center minH="40vh"><Spinner size="xl" /></Center>;
  }

  return (
    <Center minH="60vh" px={4}>
      <Box maxW="md" w="100%" borderWidth={1} borderRadius="lg" p={6} bg="white">
        <VStack align="stretch" spacing={4}>
          <Text fontSize="xl" fontWeight="bold">
            {pendingReview ? 'Account pending review' : 'Verify your patient portal access'}
          </Text>
          {pendingReview ? (
            <Text color="gray.700">
              Your request is pending staff review.
            </Text>
          ) : (
            <>
              <Text color="gray.700">
                Choose how you want to receive your confirmation code.
              </Text>
              <Button colorScheme="blue" onClick={() => requestCode('email')} isLoading={loading} isDisabled={loading || !userId}>
                {sent && sentMethod === 'email' ? 'Resend code by email' : 'Send code by email'}
              </Button>
              <Button colorScheme="teal" onClick={() => requestCode('sms')} isLoading={loading} isDisabled={loading || !userId}>
                {sent && sentMethod === 'sms' ? 'Resend code by text' : 'Send code by text'}
              </Button>
              {sent && (
                <>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit code"
                    inputMode="numeric"
                  />
                  <Button colorScheme="green" onClick={confirmCode} isLoading={loading} isDisabled={code.length !== 6}>
                    Confirm
                  </Button>
                </>
              )}
            </>
          )}
        </VStack>
      </Box>
    </Center>
  );
}
